/* RESPONSIBLE TEAM: team-standalone */
import EditorConfig, {
  type PathConfigGenerationParams,
  type StepConfigGenerationParams,
} from 'embercom/objects/visual-builder/configuration/editor';
import { NewZendeskTicketPathConfig } from 'embercom/objects/visual-builder/configuration/path';
import type Step from 'embercom/models/operator/visual-builder/step';
import { StandaloneChatMessageConfiguration } from 'embercom/objects/visual-builder/configuration/step/chat-message';
import { StandaloneZendeskTicketFinStepConfig } from 'embercom/objects/visual-builder/configuration/step/fin';
import { StandaloneAttributeCollectorConfiguration } from 'embercom/objects/visual-builder/configuration/step/attribute-collector';
import { stepTypes } from 'embercom/objects/visual-builder/configuration-list';
import HandoffToZendeskAgentConfiguration from '../step/handoff-to-zendesk-agent';
import HandoffToExternalAgentConfiguration from '../step/handoff-to-external-agent';
import { NEW_ZENDESK_TICKET_TARGET } from 'embercom/lib/operator/custom-bots/constants';
import type FinStandaloneService from 'embercom/services/fin-standalone-service';
import type Workflow from 'embercom/models/operator/visual-builder/workflow';
import type Integration from 'embercom/models/standalone/integration';
// eslint-disable-next-line no-restricted-imports
import { getFinStandaloneService } from 'embercom/lib/container-lookup';

export default class NewZendeskTicketBotConfig extends EditorConfig {
  private finStandaloneService: FinStandaloneService;

  constructor({
    areStepPaywallsActive,
    workflow,
  }: {
    areStepPaywallsActive?: boolean;
    workflow?: Workflow;
  } = {}) {
    super({ areStepPaywallsActive, workflow });
    this.finStandaloneService = getFinStandaloneService();
  }

  generatePathConfig({ path, editorState }: PathConfigGenerationParams) {
    return new NewZendeskTicketPathConfig({
      path,
      editorState,
      supportsLocalAttributes: this.supportsLocalAttributes,
    });
  }

  generateStepConfig<S extends Step>({ step, pathConfig }: StepConfigGenerationParams<S>) {
    let stepConfigArgs = { step, pathConfig };

    switch (step.typeKey) {
      case stepTypes.attributeCollector:
        return new StandaloneAttributeCollectorConfiguration(stepConfigArgs);
      case stepTypes.chatMessage:
        return new StandaloneChatMessageConfiguration({
          ...stepConfigArgs,
          attributeService: this.attributeService,
        });
      case stepTypes.fin:
        return new StandaloneZendeskTicketFinStepConfig(stepConfigArgs);
      case stepTypes.handoffToZendeskAgent:
        return new HandoffToZendeskAgentConfiguration({
          ...stepConfigArgs,
          tooltipCopy: this.intl.t(
            'operator.workflows.visual-builder.step-menu.handoff-to-zendesk-agent-tooltip-zd-tickets',
          ),
          workflowTarget: NEW_ZENDESK_TICKET_TARGET,
        });
      case stepTypes.handoffToExternalAgent: {
        let targetChannels =
          pathConfig.editorState.contentEditorService.activeObject?.targetChannels || [];

        let integration = this.integrations.find((integration) =>
          targetChannels.some((channel: string) =>
            integration.supportsExternalAgentHandoverFromChannels?.includes(channel),
          ),
        );

        return new HandoffToExternalAgentConfiguration({
          ...stepConfigArgs,
          integrationId: integration?.integrationId ?? undefined,
        });
      }
      default:
        return super.generateStepConfig(stepConfigArgs);
    }
  }

  get supportsLocalAttributes(): boolean {
    return true;
  }

  get supportsConversationChannelTargeting(): boolean {
    return false;
  }

  get supportsSideSheet(): boolean {
    return true;
  }

  get integrations(): Integration[] {
    return this.finStandaloneService.standaloneIntegrations;
  }
}
